import { useNavigate } from "@tanstack/react-router";

import { useSectionSearch, type SectionSearch } from "@/routes/search";

/**
 * Which record the URL says is open, and how to change it.
 *
 * Beside `useActiveSection` rather than with the drawer that renders it: this
 * is URL state, and every other reader and writer of the section's search
 * params lives in this directory. A list never writes the parameter by hand.
 *
 * Every write patches the existing search rather than replacing it, which is
 * what keeps opening a record from discarding the filter, the columns and the
 * sort that found it.
 */
export function useOpenRecord(): {
  recordId: string | null;
  open: (id: string) => void;
  close: () => void;
} {
  const navigate = useNavigate();
  const search = useSectionSearch();

  return {
    recordId: search.record ?? null,
    open: (id) => {
      void navigate({
        to: ".",
        search: (prev: SectionSearch) => ({ ...prev, record: id }),
      });
    },
    close: () => {
      /*
        `replace` so the drawer does not stack history entries. Opening four
        records in a row and pressing Back should return to the list, not walk
        back through the three you already closed.
      */
      void navigate({
        to: ".",
        replace: true,
        search: (prev: SectionSearch) => ({ ...prev, record: undefined }),
      });
    },
  };
}
